import { MAIN_IDB_STORE } from '../../util/browser/idb';

import type {
  AuditEvent,
  CaseRoom,
  EvidenceRecord,
  IntelNote,
  InternalTicket,
  InvestigationDb,
  InvestigationSettings,
  IocIndexEntry,
  LocalNotificationRecord,
} from './types';
import { getGlobal } from '../../global';
import { isCentralDbEnabled } from '../centralDb/config';
import { pushAuditToCentral, scheduleInvestigationSnapshotPush } from '../centralDb/sync';
import { shouldPauseRasiBackgroundWork } from '../rasitechPerf';
import { createEmptyInvestigationDb } from './types';

const DB_KEY = 'rasi-investigation-v1';
const EVIDENCE_BLOB_PREFIX = 'rasi-evidence-blob:';

const MAX_AUDIT = 15000;
const MAX_IOC_ENTRIES_PER_KEY = 200;

let dbCache: InvestigationDb | undefined;
let loadPromise: Promise<InvestigationDb> | undefined;

export async function loadInvestigationDb(): Promise<InvestigationDb> {
  if (dbCache) return dbCache;
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    const stored = await MAIN_IDB_STORE.get<InvestigationDb>(DB_KEY);
    dbCache = stored?.version === 1 ? stored : createEmptyInvestigationDb();
    if (!stored || stored.version !== 1) {
      await persistInvestigationDb();
    }
    return dbCache!;
  })();

  return loadPromise as Promise<InvestigationDb>;
}

function mergeRecordMaps<T extends { id: string; updatedAt?: number }>(
  local: Record<string, T>,
  remote: Record<string, T> | undefined,
  getTs: (item: T) => number = (item) => item.updatedAt || 0,
): Record<string, T> {
  if (!remote) return local;
  const out = { ...local };
  Object.values(remote).forEach((item) => {
    const existing = out[item.id];
    if (!existing || getTs(item) >= getTs(existing)) {
      out[item.id] = item;
    }
  });
  return out;
}

function mergeAuditEvents(local: AuditEvent[], remote: AuditEvent[] | undefined): AuditEvent[] {
  if (!remote?.length) return local;
  const map = new Map<string, AuditEvent>();
  local.forEach((e) => map.set(e.id, e));
  remote.forEach((e) => map.set(e.id, e));
  return [...map.values()].sort((a, b) => a.at - b.at).slice(-MAX_AUDIT);
}

function mergeIocIndex(
  local: InvestigationDb['iocIndex'],
  remote: InvestigationDb['iocIndex'] | undefined,
): InvestigationDb['iocIndex'] {
  if (!remote) return local;
  const out = { ...local };
  Object.entries(remote).forEach(([key, entries]) => {
    const list = [...(out[key] || [])];
    entries.forEach((entry) => {
      const exists = list.some((e) => e.chatId === entry.chatId
        && e.messageId === entry.messageId && e.seenAt === entry.seenAt);
      if (!exists) list.push(entry);
    });
    if (list.length > MAX_IOC_ENTRIES_PER_KEY) {
      list.splice(0, list.length - MAX_IOC_ENTRIES_PER_KEY);
    }
    out[key] = list;
  });
  return out;
}

const lastRemoteInvestigationPullByUser: Record<string, number> = {};

export async function mergeRemoteInvestigationSnapshot(
  remote: InvestigationDb,
  remoteUpdatedAt: number,
  userId?: string,
): Promise<boolean> {
  if (!remote?.version) return false;
  const uid = userId || 'default';
  if (remoteUpdatedAt <= (lastRemoteInvestigationPullByUser[uid] || 0)) return false;

  await loadInvestigationDb();
  const local = dbCache!;

  dbCache = {
    ...local,
    settings: { ...local.settings, ...remote.settings },
    cases: mergeRecordMaps(local.cases, remote.cases),
    notes: mergeRecordMaps(local.notes, remote.notes),
    tickets: mergeRecordMaps(local.tickets, remote.tickets),
    evidence: mergeRecordMaps(local.evidence, remote.evidence, (e) => e.uploadedAt),
    auditEvents: mergeAuditEvents(local.auditEvents, remote.auditEvents),
    iocIndex: mergeIocIndex(local.iocIndex, remote.iocIndex),
    deviceSessions: mergeByIdArray(local.deviceSessions, remote.deviceSessions, (s) => s.fingerprint),
    notifications: mergeByIdArray(local.notifications, remote.notifications, (n) => n.id, 500),
    ipCache: { ...local.ipCache, ...remote.ipCache },
  };

  lastRemoteInvestigationPullByUser[uid] = remoteUpdatedAt;
  await persistInvestigationDb();
  return true;
}

function mergeByIdArray<T extends { id: string }>(
  local: T[],
  remote: T[] | undefined,
  idOf: (item: T) => string,
  cap?: number,
): T[] {
  if (!remote?.length) return local;
  const map = new Map<string, T>();
  local.forEach((item) => map.set(idOf(item), item));
  remote.forEach((item) => map.set(idOf(item), item));
  const merged = [...map.values()];
  return cap ? merged.slice(0, cap) : merged;
}

export function getInvestigationDb(): InvestigationDb {
  if (!dbCache) {
    dbCache = createEmptyInvestigationDb();
  }
  return dbCache;
}

export async function persistInvestigationDb(): Promise<void> {
  if (!dbCache) return;
  await MAIN_IDB_STORE.set(DB_KEY, dbCache);
  if (isCentralDbEnabled() && !shouldPauseRasiBackgroundWork(getGlobal())) {
    scheduleInvestigationSnapshotPush(() => dbCache);
  }
}

export async function updateInvestigationDb(
  updater: (db: InvestigationDb) => void,
): Promise<InvestigationDb> {
  await loadInvestigationDb();
  updater(dbCache!);
  await persistInvestigationDb();
  return dbCache!;
}

export function generateId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export async function appendAuditEvent(event: AuditEvent): Promise<void> {
  await updateInvestigationDb((db) => {
    db.auditEvents.push(event);
    if (db.auditEvents.length > MAX_AUDIT) {
      db.auditEvents = db.auditEvents.slice(-MAX_AUDIT);
    }
  });
  if (isCentralDbEnabled()) {
    void pushAuditToCentral(event, event.userId);
  }
}

export async function indexIocEntry(entry: IocIndexEntry): Promise<void> {
  const key = `${entry.type}:${entry.normalized}`;
  await updateInvestigationDb((db) => {
    const list = db.iocIndex[key] || [];
    const exists = list.some((e) => e.chatId === entry.chatId
      && e.messageId === entry.messageId && e.seenAt === entry.seenAt);
    if (!exists) {
      list.push(entry);
      if (list.length > MAX_IOC_ENTRIES_PER_KEY) {
        list.splice(0, list.length - MAX_IOC_ENTRIES_PER_KEY);
      }
      db.iocIndex[key] = list;
    }
  });
}

export async function upsertCase(caseRoom: CaseRoom): Promise<void> {
  await updateInvestigationDb((db) => {
    db.cases[caseRoom.id] = caseRoom;
  });
}

export async function deleteCase(caseId: string): Promise<void> {
  await updateInvestigationDb((db) => {
    delete db.cases[caseId];
  });
}

export async function upsertEvidence(record: EvidenceRecord): Promise<void> {
  await updateInvestigationDb((db) => {
    db.evidence[record.id] = record;
  });
}

export async function saveEvidenceBlob(id: string, blob: Blob): Promise<void> {
  await MAIN_IDB_STORE.set(`${EVIDENCE_BLOB_PREFIX}${id}`, blob);
}

export async function loadEvidenceBlob(id: string): Promise<Blob | undefined> {
  return MAIN_IDB_STORE.get<Blob>(`${EVIDENCE_BLOB_PREFIX}${id}`);
}

export async function upsertNote(note: IntelNote): Promise<void> {
  await updateInvestigationDb((db) => {
    db.notes[note.id] = note;
  });
}

export async function deleteNote(noteId: string): Promise<void> {
  await updateInvestigationDb((db) => {
    delete db.notes[noteId];
  });
}

export async function upsertTicket(ticket: InternalTicket): Promise<void> {
  await updateInvestigationDb((db) => {
    db.tickets[ticket.id] = ticket;
  });
}

export async function pushNotification(n: LocalNotificationRecord): Promise<void> {
  await updateInvestigationDb((db) => {
    db.notifications.unshift(n);
    if (db.notifications.length > 500) {
      db.notifications = db.notifications.slice(0, 500);
    }
  });
}

export async function updateInvestigationSettings(
  patch: Partial<InvestigationSettings>,
): Promise<InvestigationSettings> {
  const db = await updateInvestigationDb((d) => {
    d.settings = { ...d.settings, ...patch };
  });
  return db.settings;
}

export function listCases(): CaseRoom[] {
  return Object.values(getInvestigationDb().cases)
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

export function listAuditEvents(limit = 500): AuditEvent[] {
  return getInvestigationDb().auditEvents.slice(-limit).reverse();
}

export function listEvidence(): EvidenceRecord[] {
  return Object.values(getInvestigationDb().evidence)
    .sort((a, b) => b.uploadedAt - a.uploadedAt);
}

export function listNotes(): IntelNote[] {
  return Object.values(getInvestigationDb().notes)
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

export function listTickets(): InternalTicket[] {
  return Object.values(getInvestigationDb().tickets)
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

export async function exportInvestigationJson(): Promise<string> {
  await loadInvestigationDb();
  const db = getInvestigationDb();
  return JSON.stringify({
    exportedAt: Date.now(),
    ...db,
    evidence: Object.fromEntries(
      Object.entries(db.evidence).map(([id, e]) => [id, { ...e, blobOmitted: true }]),
    ),
  }, null, 2);
}
